import React, { useEffect, useState, useRef } from 'react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { SocialProofSection } from './components/SocialProofSection';
import { StepsSection } from './components/StepsSection';
import { SplitSection } from './components/SplitSection';
import { DifferenceSection } from './components/DifferenceSection';
import { WaitlistSection } from './components/WaitlistSection';
import { Footer } from './components/Footer';
import { ElephantMascot } from './components/ElephantMascot';
import { Button } from './components/common/Button';
import { Star, Circle, Triangle } from 'lucide-react';
export function App() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    document.title = 'Kraya';
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect(); // Only trigger once
      }
    }, {
      threshold: 0.6 // Wait until the section is 60% visible (more centered) before triggering
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white text-[#1A1A1A] font-sans selection:bg-[#FFFC00] selection:text-[#1A1A1A]" data-id="element-0">
      <Header data-id="element-1" />
      <main data-id="element-2">
        <HeroSection data-id="element-3" />
        <SocialProofSection data-id="element-4" />
        <StepsSection data-id="element-5" />

        {/* Invite Only Mascot Section */}
        <section ref={sectionRef} className="bg-[#D4F57A] py-16 border-y-4 border-[#1A1A1A] relative overflow-hidden z-20" data-id="element-6">
          <style data-id="element-7">{`
            @keyframes tetris-drop {
              0% { transform: translateY(-150px); opacity: 0; }
              100% { transform: translateY(0); opacity: 1; }
            }
            .tetris-pill {
              opacity: 0;
            }
            .tetris-pill.animate {
              animation: tetris-drop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            }
          `}</style>

          <div className="max-w-6xl mx-auto px-4 flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-0 mt-8" data-id="element-8">
            {/* Left Side Pills */}
            <div className="hidden lg:flex flex-col gap-6 items-end w-72 -mr-16 relative z-20" data-id="element-9">
              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56`} style={{
              animationDelay: '0.8s'
            }} data-id="element-10">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is my data safe?'
                }));
              }} className="bg-[#FF90E8] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[6deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-11">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-12">
                    Is my data safe?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 mr-4`} style={{
              animationDelay: '0.6s'
            }} data-id="element-13">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Invite-only club'
                }));
              }} className="bg-[#4DE1C1] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[6deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-14">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-15">
                    Wait, how do I get an invite?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56 mr-14`} style={{
              animationDelay: '0.4s'
            }} data-id="element-16">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is this too good to be true?'
                }));
              }} className="bg-[#FFFC00] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[3deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-17">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-18">
                    Is this too good to be true?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 mr-12`} style={{
              animationDelay: '0.2s'
            }} data-id="element-19">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Truly Automatic'
                }));
              }} className="bg-[#B28DFF] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[4deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-20">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-21">
                    How do I redeem?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-72 mr-10`} style={{
              animationDelay: '0s'
            }} data-id="element-22">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Chill with Friends'
                }));
              }} className="bg-[#FFB067] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[2deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-23">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-24">
                    Can I invite my friends?
                  </p>
                </div>
              </div>
            </div>

            {/* Center Content */}
            <div className="w-full max-w-sm flex flex-col items-center justify-center relative z-10 shrink-0" data-id="element-25">
              <ElephantMascot size="md" direction="right" className="mb-6 hover:scale-110 transition-transform" data-id="element-26" />
              <div className="bg-[#1A1A1A] rounded-3xl p-8 text-white relative shadow-[8px_8px_0px_0px_rgba(0,0,0,0.2)] w-full" data-id="element-27">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-b-[16px] border-b-[#1A1A1A]" data-id="element-28" />
                <h3 className="text-3xl font-black mb-4 font-display tracking-tight" data-id="element-29">
                  This is an exclusive club.
                </h3>
                <p className="text-lg mb-8 text-gray-300" data-id="element-30">
                  Kraya is currently invite-only. Get an invite code from a
                  friend or join the waitlist to get early access.
                </p>
                <Button variant="primary" className="w-full text-lg py-4" onClick={() => window.open('https://kraya.live/', '_blank')} data-id="element-31">
                  Get Invite Code
                </Button>
              </div>
            </div>

            {/* Right Side Pills */}
            <div className="hidden lg:flex flex-col gap-6 items-start w-72 -ml-16 relative z-20" data-id="element-32">
              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-56`} style={{
              animationDelay: '0.9s'
            }} data-id="element-33">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Gift an Experience'
                }));
              }} className="bg-[#FFFC00] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[3deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-34">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-35">
                    Can I gift perks?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 ml-4`} style={{
              animationDelay: '0.7s'
            }} data-id="element-36">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: "My favorite café isn't on the list"
                }));
              }} className="bg-[#B28DFF] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[8deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-37">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-38">
                    I don't see my fav cafe?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-64 ml-8`} style={{
              animationDelay: '0.5s'
            }} data-id="element-39">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Is my payment information secure?'
                }));
              }} className="bg-[#FF90E8] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] -rotate-[4deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-40">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-41">
                    Is my payment secure?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 ml-2`} style={{
              animationDelay: '0.3s'
            }} data-id="element-42">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'When will it be in my city?'
                }));
              }} className="bg-[#FFB067] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[5deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-43">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-44">
                    When will it be in my city?
                  </p>
                </div>
              </div>

              <div className={`tetris-pill ${isVisible ? 'animate' : ''} w-60 ml-10`} style={{
              animationDelay: '0.1s'
            }} data-id="element-45">
                <div onClick={e => {
                e.preventDefault();
                const faqSection = document.getElementById('faq');
                if (faqSection) faqSection.scrollIntoView({
                  behavior: 'smooth'
                });
                window.dispatchEvent(new CustomEvent('trigger-faq-highlight', {
                  detail: 'Surprise Perks'
                }));
              }} className="bg-[#45EBA5] border-4 border-[#1A1A1A] rounded-full py-3 px-6 shadow-[4px_4px_0px_0px_rgba(26,26,26,1)] rotate-[2deg] hover:rotate-0 hover:scale-105 transition-all cursor-pointer" data-id="element-46">
                  <p className="font-bold text-[#1A1A1A] text-center" data-id="element-47">
                    What else can I expect?
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SplitSection data-id="element-48" />
        <DifferenceSection data-id="element-49" />
        <WaitlistSection data-id="element-50" />
      </main>
      <Footer data-id="element-51" />
    </div>;
}